// Components
import React from "react";

// Utils
import tileImageMap from "../../utils/imageMap";
import { softenHex } from "../../utils/colorUtils";

// Styling
import styles from "./Thumbnail.module.css";

// Consider anything ≤3 chars without a dot an "emoji"
function isEmoji(str: unknown): boolean {
  return typeof str === "string" && str.length <= 3 && !str.includes(".");
}

function isUrl(str: unknown): boolean {
  return (
    typeof str === "string" &&
    (str.startsWith("http://") || str.startsWith("https://"))
  );
}

export default function Thumbnail({
  imgName,
  alt = "Image",
  bgColor,
  size = "medium",
  style = {},
  onClick = undefined,
  className = "",
  "data-tip": tip,
}: {
  imgName?: string | null;
  alt?: string;
  bgColor?: string | null;
  size?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  className?: string;
  /** The tooltip (kast-common's TooltipHost shows it). */
  "data-tip"?: string;
}) {
  // Resolve image URL: first try URL, then emoji, else map lookup
  let imageSrc: string | null = null;
  if (typeof imgName === "string" && isUrl(imgName)) {
    imageSrc = imgName;
  } else if (isEmoji(imgName)) {
    // no <img>, we'll render the emoji
  } else if (typeof imgName === "string") {
    let processedImgName = imgName;
    if (imgName.includes(".")) {
      processedImgName = imgName.split(".")[0];
    }
    const key = processedImgName.toLowerCase();
    imageSrc = tileImageMap[key]?.[0] ?? null;
  }

  const appliedStyle = {
    background:
      bgColor && bgColor !== "none" ? softenHex(bgColor, 0.55) : undefined,
    ...style,
  };

  return (
    <div
      className={`${styles.thumbnail} ${styles[size]} ${className} ${onClick ? styles.clickable : ""}`}
      style={appliedStyle}
      onClick={onClick}
      data-tip={tip}
    >
      {isUrl(imgName) ? (
        <img
          src={imageSrc ?? undefined}
          alt={alt}
          className={styles.image}
          referrerPolicy="no-referrer"
        />
      ) : isEmoji(imgName) ? (
        <span className={styles.emoji}>{imgName}</span>
      ) : (
        imageSrc && <img src={imageSrc} alt={alt} className={styles.image} />
      )}
    </div>
  );
}
